import { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { useFlag } from 'src/hooks/flag';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import { getCurrentUserUuid } from 'src/selectors/user-selectors';
import { useMutation } from 'src/utils/fb-query';
import { authRequest } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { getPipelinePageURL } from '../../../utils/links';
import { messages } from '../messages';

export type FixFlakyTestResponse = {
  build_number: number;
  pipeline_uuid?: string;
};

async function fixFlakyTestRequest(
  repositoryPath: string,
  testCaseUuid: string,
  userUuid: string | null | undefined
): Promise<FixFlakyTestResponse> {
  const baseUrl = envBaseUrl('/!api/internal');
  let url = `${baseUrl}/repositories/${repositoryPath}/tests/${testCaseUuid}/actions/fix-flaky-test`;
  if (userUuid) {
    url = `${url}?actor=${encodeURIComponent(userUuid)}`;
  }

  const response = await fetch(authRequest(url, { method: 'POST' }));

  if (!response.ok) {
    throw new Error(
      `Failed to fix flaky test: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
}

export type UseFixFlakyTestParams = {
  onSuccess?: () => void;
};

export const useFixFlakyTest = ({ onSuccess }: UseFixFlakyTestParams = {}) => {
  const { showFlag, showErrorFlag } = useFlag();
  const { formatMessage } = useIntl();
  const repositoryPath = useSelector(getCurrentRepositoryFullSlug) || '';
  const userUuid = useSelector(getCurrentUserUuid);

  const fixFlakyTestCallback = useCallback(
    (testCaseUuid: string) => {
      if (repositoryPath == null || repositoryPath === '') {
        showErrorFlag(formatMessage(messages.fixFlakyError));
        return Promise.reject(new Error('Repository path is not available'));
      }
      return fixFlakyTestRequest(repositoryPath, testCaseUuid, userUuid);
    },
    [repositoryPath, userUuid, showErrorFlag, formatMessage]
  );

  const { mutate: fixFlakyTest, isPending: isFixingFlakyTest } = useMutation(
    fixFlakyTestCallback,
    {
      onSuccess: (data: FixFlakyTestResponse) => {
        onSuccess?.();
        const buildNumber = data.build_number;
        const pipelineUrl = getPipelinePageURL({ buildNumber }, repositoryPath);
        showFlag({
          id: `fix-flaky-test-success-${Date.now()}`,
          iconType: 'success',
          title: formatMessage(messages.fixFlakyPipelineSuccess, {
            number: buildNumber,
          }),
          actions: [
            {
              content: formatMessage(messages.viewPipelineNumber, {
                number: buildNumber,
              }),
              href: pipelineUrl,
            },
          ],
        });
      },
      onError: (error: Error) => {
        captureExceptionWithTags(error, {
          section: 'pipelines-test-management',
          action: 'fix-flaky-test',
        });
        showErrorFlag(formatMessage(messages.fixFlakyError));
      },
    }
  );

  return {
    fixFlakyTest,
    isFixingFlakyTest,
  };
};
